import { resolveApiOrigin } from '../../api/api-origin.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useDesignDashboard,
  type ChatMessage,
  type DashboardDatasetContext,
} from '../../api/copilot.js';
import {
  useMintKioskToken,
  usePublishDashboardVersion,
  useSaveDashboardDraft,
  useDiscardDashboardDraft,
  type DashboardDefinition,
  type DatasetBinding,
} from '../../api/dashboards.js';
import { useDatasets } from '../../api/datasets.js';
import type { DatasetSummary, RenderLibrary } from '../../api/types.js';
import { FullscreenPreview } from '../reports/FullscreenPreview.js';
import { useSplitPaneSize } from '../../shell/useSplitPane.js';
import { SplitDivider } from '../../shell/SplitDivider.js';
import { consumeStarterPrompt } from '../frameworks/starter-prompt-seed.js';
import { useSaveAsFramework } from '../../api/frameworks.js';
import { useDraftAutosave } from './useDraftAutosave.js';
import { refreshLabel } from './refresh-label.js';

/**
 * AiDashboardDesigner — conversational authoring for dashboards,
 * matching AiDesignerPanel's shape but with:
 *   - multi-dataset binding sidebar (add / edit / remove)
 *   - kiosk-aware preview (via a minted preview token)
 *   - no XLSX tab (dashboards don't export)
 */
export function AiDashboardDesigner({
  dashboardId,
  definition,
  draftSavedAt = null,
  displayMode,
  refreshIntervalSec,
  currentVersion,
}: {
  dashboardId: string;
  /** The draft when one exists, else the published version (CSAGE-496). */
  definition: DashboardDefinition;
  /** When `definition` is an unpublished draft: when it was last saved. */
  draftSavedAt?: Date | null;
  displayMode: 'dashboard' | 'kiosk';
  refreshIntervalSec: number;
  currentVersion: number;
}): JSX.Element {
  const { data: datasetCatalog } = useDatasets();
  const design = useDesignDashboard();
  const publish = usePublishDashboardVersion(dashboardId);
  const saveDraft = useSaveDashboardDraft(dashboardId);
  const discardDraft = useDiscardDashboardDraft(dashboardId);
  const mintPreviewToken = useMintKioskToken(dashboardId);
  const saveAsFramework = useSaveAsFramework();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(draftSavedAt);
  const chatSplit = useSplitPaneSize('dash-designer.chat', 26, 15, 45);

  // AiDashboardDesigner is the HTML-mode designer. DashboardDetail
  // routes widget-mode dashboards to the WidgetDashboardDesigner, so
  // any html-only field access here is guarded by a kind check.
  const htmlLayout = definition.layout.kind === 'html' ? definition.layout : null;
  const [title, setTitle] = useState(definition.layout.title);
  const [html, setHtml] = useState(htmlLayout?.html ?? '');
  const [css, setCss] = useState(htmlLayout?.css ?? '');
  const [libraries, setLibraries] = useState<RenderLibrary[]>(htmlLayout?.libraries ?? []);
  const [bindings, setBindings] = useState<DatasetBinding[]>(definition.layout.datasets);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState(() => consumeStarterPrompt('dashboard', dashboardId) ?? '');
  const [pane, setPane] = useState<'preview' | 'html' | 'css'>('preview');
  // Panels default to collapsed so the canvas + preview iframe get the
  // full screen until the user explicitly opens an authoring tool. Each
  // panel's open/closed state is persisted to localStorage so the user's
  // preference is sticky across navigations. Opening from the toolbar
  // also auto-saves the starter-prompt seed (if there is one) and pops
  // chat open so the user lands in their seeded conversation.
  const [chatCollapsed, setChatCollapsed] = usePersistedFlag('dash.designer.chat.collapsed', true);
  const [datasetsOpen, setDatasetsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [librariesOpen, setLibrariesOpen] = useState(false);
  const librariesBtnRef = useRef<HTMLButtonElement | null>(null);
  const [fsPreview, setFsPreview] = useState(false);

  // If the user landed here with a starter prompt (created from a
  // framework / "describe…" entry point), force chat open the first
  // time so they're not staring at a blank preview wondering where
  // their prompt went.
  useEffect(() => {
    if (prompt && chatCollapsed) setChatCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // Mint a designer preview token once on mount.
  useEffect(() => {
    if (previewToken) return;
    mintPreviewToken.mutate(
      { label: 'designer preview' },
      { onSuccess: (res) => setPreviewToken(res.token) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewUrl = useMemo(() => {
    if (!previewToken) return null;
    // Preview uses ?draft=1 so it renders the current draft (if any).
    // Public kiosk viewers without that flag continue to see the last
    // published version — drafts never leak to the NOC wall.
    //
    // Anchor on the API origin (VITE_API_BASE_URL) when set, so split-
    // host deploys (sage.gocanopy.io ↔ api.sage.gocanopy.io) hit the
    // API host directly. A relative URL would resolve against the SPA
    // host, where nginx falls back to index.html for unknown paths
    // and the iframe ends up rendering the SPA instead of the
    // dashboard.
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    const bust = cacheBust(html, css, libraries, bindings);
    return `${apiOrigin}/reporting/api/kiosk/dashboards/${dashboardId}/view?token=${encodeURIComponent(previewToken)}&draft=1&_=${bust}`;
  }, [dashboardId, previewToken, html, css, libraries, bindings]);

  // Save edits into the DRAFT buffer (not a new version). Debounced
  // 500ms so typing doesn't thrash the DB. Publishing is an explicit
  // user action via the button — it promotes the draft to a version and
  // clears the draft. Live preview reads the draft via ?draft=1 above.
  // The baseline is the state the designer opened with, so opening it
  // saves nothing (CSAGE-496).
  const draftAutosave = useDraftAutosave(
    htmlSnapshot({ title, html, css, libraries, bindings }),
    500,
    () => {
      void saveDraft
        .mutateAsync({
          definition: {
            schemaVersion: '1.0.0',
            layout: {
              kind: 'html',
              title,
              html,
              ...(css ? { css } : {}),
              ...(libraries.length > 0 ? { libraries } : {}),
              datasets: bindings,
            },
          },
        })
        .then(() => {
          setHasUnsavedChanges(false);
          setLastSavedAt(new Date());
          if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
        })
        .catch(() => {
          /* surfaced inline via saveDraft.error */
        });
    },
    () => setHasUnsavedChanges(true),
    () => setHasUnsavedChanges(false),
  );

  useEffect(() => {
    // Don't autoscroll on mount when the chat is empty — the chatEndRef
    // can sit below the viewport (designer is tall) and scrollIntoView
    // would yank the WHOLE page down, hiding the top tabs / publish
    // controls. Only autoscroll once real messages exist or Claude is
    // streaming a reply. Scope to the nearest scrollable ancestor so a
    // scrollable chat panel doesn't move the surrounding page either.
    if (messages.length === 0 && !design.isPending) return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length, design.isPending]);

  async function submitPrompt(): Promise<void> {
    if (!prompt.trim() || design.isPending) return;
    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: prompt.trim() }];
    setMessages(newMessages);
    setPrompt('');

    // Resolve dataset context from the catalog. For each binding, feed
    // Claude what columns exist (if inferrable) + a synthetic sample.
    const datasetsCtx: DashboardDatasetContext[] = bindings.map((b) => {
      const catalogEntry = datasetCatalog?.find((d) => d.name === b.datasetName);
      const knownColumns: string[] =
        b.columns ?? catalogEntry?.columns.map((c: { name: string }) => c.name) ?? [];
      return {
        key: b.key,
        datasetName: b.datasetName,
        columns: knownColumns,
        sampleRows: mockSamples(knownColumns),
      };
    });

    try {
      const result = await design.mutateAsync({
        sessionId,
        messages: newMessages,
        displayMode,
        refreshIntervalSec,
        datasets: datasetsCtx,
      });
      setSessionId(result.sessionId);
      if (result.html) setHtml(result.html);
      if (result.css !== null) setCss(result.css);
      if (result.libraries) setLibraries(result.libraries);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: summarizeAssistant(result.assistantMessage) },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `⚠️ ${(err as Error & { body?: { message?: string } }).body?.message ?? (err as Error).message}`,
        },
      ]);
    }
  }

  function toggleLib(lib: RenderLibrary): void {
    setLibraries((prev) => (prev.includes(lib) ? prev.filter((l) => l !== lib) : [...prev, lib]));
  }

  const dashCols = (chatCollapsed ? '0 0 ' : `${chatSplit.sizePct}% 4px `) + '1fr';

  return (
    <div className="card-body" style={{ padding: 0 }}>
      <div
        className={
          'dash-designer' +
          (chatCollapsed ? ' dash-designer--chat-collapsed' : '') +
          (chatSplit.isDragging ? ' split-dragging' : '')
        }
        ref={(el) => {
          chatSplit.attachContainer(el);
        }}
        style={{ gridTemplateColumns: dashCols }}
      >
        {/* Floating reopen buttons were replaced by proper toolbar
            toggles in the canvas header — see the toolbar group below.
            Reopen logic lives entirely in the toolbar now. */}

        {/* LEFT: chat */}
        <div className="ai-chat">
          <div className="ai-chat-header">
            <div className="text-sm text-dim">
              {displayMode === 'kiosk' ? 'Kiosk view' : 'Dashboard view'} ·{' '}
              {refreshLabel(refreshIntervalSec)}
            </div>
            <div className="row" style={{ gap: 6 }}>
              {sessionId && (
                <span className="text-mute text-xs cell-mono">{sessionId.slice(0, 8)}…</span>
              )}
              <button
                type="button"
                className="btn-icon"
                onClick={() => setChatCollapsed(true)}
                title="Collapse chat"
                aria-label="Collapse chat"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 4L6 8l4 4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Libraries panel moved into a popover from the canvas
              toolbar so the chat column stays focused on conversation.
              Help / quick-ref moved into the Help modal. */}

          <div className="ai-chat-log">
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                <div className="ai-chat-empty-spark" aria-hidden>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
                  </svg>
                </div>
                <h3>Describe the dashboard</h3>
                <p className="ai-chat-empty-lede">
                  Tell Claude what this wall should show. It will generate the HTML, CSS, and bind
                  to your datasets. Iterate with follow-ups.
                </p>

                <div className="ai-example-header">Start from an example</div>
                <ul className="ai-example-prompts">
                  {STARTER_PROMPTS.map((p) => (
                    <li
                      key={p.title}
                      className="ai-example-card"
                      onClick={() => setPrompt(p.prompt)}
                    >
                      <div className="ai-example-card-icon" aria-hidden>
                        {p.icon}
                      </div>
                      <div className="ai-example-card-body">
                        <div className="ai-example-card-title">{p.title}</div>
                        <div className="ai-example-card-desc">{p.desc}</div>
                      </div>
                      <div className="ai-example-card-arrow" aria-hidden>
                        →
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={'ai-msg ai-msg--' + m.role}>
                <div className="ai-msg-role">{m.role === 'user' ? 'You' : 'Claude'}</div>
                <div className="ai-msg-body">{m.content}</div>
              </div>
            ))}
            {design.isPending && (
              <div className="ai-msg ai-msg--assistant">
                <div className="ai-msg-role">Claude</div>
                <div className="ai-msg-body">
                  <span className="ai-typing" /> designing…
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="ai-chat-input">
            <textarea
              className="textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void submitPrompt();
                }
              }}
              placeholder="Describe the dashboard. Cmd/Ctrl+Enter to send."
              rows={3}
              style={{ fontFamily: 'inherit', fontSize: 'var(--font-size-md)' }}
            />
            <div className="row row--between" style={{ marginTop: 6 }}>
              <span className="text-mute text-xs">Cmd+Enter to send</span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!prompt.trim() || design.isPending}
                onClick={() => void submitPrompt()}
              >
                {design.isPending ? 'Designing…' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Always render a slot in this grid column so the 5-column
            template stays in sync — the .dash-canvas lives in column
            3 and falls into a 0-width column if we omit the divider. */}
        {chatCollapsed ? (
          <div aria-hidden style={{ width: 0 }} />
        ) : (
          <SplitDivider
            onDragStart={chatSplit.startDrag}
            onDoubleClick={() => chatSplit.setSize(26)}
            title="Drag to resize chat · double-click to reset"
          />
        )}

        {/* CENTER: canvas */}
        <div className="dash-canvas">
          <div className="ai-canvas-header">
            {/* View-mode pill switcher — replaces the bulkier tab strip.
                Three modes (Preview / HTML / CSS) sit as a segmented
                button group so the whole toolbar fits on one line. */}
            <div className="view-mode-switch" role="tablist" aria-label="Canvas view">
              <button
                type="button"
                role="tab"
                aria-selected={pane === 'preview'}
                className={'view-mode-btn' + (pane === 'preview' ? ' view-mode-btn--on' : '')}
                onClick={() => setPane('preview')}
              >
                Preview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pane === 'html'}
                className={'view-mode-btn' + (pane === 'html' ? ' view-mode-btn--on' : '')}
                onClick={() => setPane('html')}
              >
                HTML
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pane === 'css'}
                className={'view-mode-btn' + (pane === 'css' ? ' view-mode-btn--on' : '')}
                onClick={() => setPane('css')}
              >
                CSS
              </button>
            </div>

            {/* Authoring-tool toggles: chat docks left, datasets dock
                right, libraries opens a popover, help opens a modal.
                Keeps the canvas full-width until the user explicitly
                pulls a tool in. */}
            <div className="designer-toolbar" role="toolbar" aria-label="Authoring tools">
              <button
                type="button"
                className={
                  'designer-toolbar-btn' + (!chatCollapsed ? ' designer-toolbar-btn--on' : '')
                }
                onClick={() => setChatCollapsed((v) => !v)}
                title={chatCollapsed ? 'Open chat (Claude designer)' : 'Close chat'}
                aria-pressed={!chatCollapsed}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M2 3h12v8H6l-4 3V3z" />
                </svg>
                <span>Chat</span>
                {messages.length > 0 && (
                  <span className="designer-toolbar-count">{messages.length}</span>
                )}
              </button>
              <button
                type="button"
                className={
                  'designer-toolbar-btn' + (datasetsOpen ? ' designer-toolbar-btn--on' : '')
                }
                onClick={() => setDatasetsOpen(true)}
                title="Open datasets — manage bindings"
                aria-haspopup="dialog"
                aria-expanded={datasetsOpen}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <ellipse cx="8" cy="3.5" rx="5" ry="1.5" />
                  <path d="M3 3.5v4.5c0 .83 2.24 1.5 5 1.5s5-.67 5-1.5V3.5" />
                  <path d="M3 8v4.5c0 .83 2.24 1.5 5 1.5s5-.67 5-1.5V8" />
                </svg>
                <span>Datasets</span>
                {bindings.length > 0 && (
                  <span className="designer-toolbar-count">{bindings.length}</span>
                )}
              </button>
              <button
                ref={librariesBtnRef}
                type="button"
                className={
                  'designer-toolbar-btn' + (librariesOpen ? ' designer-toolbar-btn--on' : '')
                }
                onClick={() => setLibrariesOpen((v) => !v)}
                title="Toggle render libraries"
                aria-haspopup="dialog"
                aria-expanded={librariesOpen}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M2.5 3.5h11v9h-11z" />
                  <path d="M5 3.5v9M11 3.5v9" />
                </svg>
                <span>Libraries</span>
                <span className="designer-toolbar-count">{libraries.length}</span>
              </button>
              <button
                type="button"
                className="designer-toolbar-btn"
                onClick={() => setHelpOpen(true)}
                title="Open Handlebars cheat sheet + examples"
                aria-haspopup="dialog"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="8" cy="8" r="6" />
                  <path d="M6.2 6.2c0-1 0.8-1.8 1.8-1.8s1.8.8 1.8 1.8c0 1.2-1.8 1.4-1.8 2.6" />
                  <path d="M8 11.2v.01" />
                </svg>
                <span>Help</span>
              </button>
            </div>

            {/* Right-cluster, fixed order matching the user's mental
                model: Fullscreen → Save as framework → Discard →
                Publish. Publish stays primary + rightmost (the "do
                it" action). Auto-save status sits as a small dot at
                the very left of the cluster — it's a status not an
                action, so it doesn't compete with the buttons. */}
            <div className="canvas-actions">
              <DraftIndicator
                saving={saveDraft.isPending}
                unsaved={hasUnsavedChanges}
                lastSavedAt={lastSavedAt}
              />
              {pane === 'preview' && previewUrl && (
                <button
                  type="button"
                  className="canvas-action-fullscreen"
                  onClick={() => setFsPreview(true)}
                  title="Open the preview full-screen (kiosk view)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M2 6V2h4" />
                    <path d="M14 6V2h-4" />
                    <path d="M2 10v4h4" />
                    <path d="M14 10v4h-4" />
                  </svg>
                  <span>Fullscreen</span>
                </button>
              )}
              {/* Save-as-framework — labeled button with a layout-grid
                  + star icon. The grid signals "reusable layout" and
                  the star signals "save for reuse" — way more
                  intuitive than the old bookmark mark. */}
              <button
                type="button"
                className="canvas-action-save"
                disabled={!html || saveAsFramework.isPending}
                onClick={() => {
                  const defaultName = title ? `${title} framework` : 'New framework';
                  const name = window.prompt(
                    'Save this dashboard as a reusable framework. Name:',
                    defaultName,
                  );
                  if (!name) return;
                  saveAsFramework.mutate({
                    name: name.trim(),
                    moduleType: 'dashboard',
                    skeleton: {
                      kind: 'html-handlebars',
                      starterPrompt: `Start from the saved "${name}" framework and iterate.`,
                      html,
                      refreshIntervalSec,
                      bindings: [],
                      outline: [],
                    },
                  });
                }}
                title="Save this layout as a reusable framework — other dashboards can start from it"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <rect x="2" y="2" width="5" height="5" rx="0.6" />
                  <rect x="9" y="2" width="5" height="5" rx="0.6" />
                  <rect x="2" y="9" width="5" height="5" rx="0.6" />
                  <path
                    d="M11.5 9.5l0.85 1.72 1.9.28-1.38 1.34.33 1.9-1.7-.9-1.7.9.33-1.9-1.38-1.34 1.9-.28z"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
                <span>{saveAsFramework.isPending ? 'Saving…' : 'Save as framework'}</span>
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={discardDraft.isPending || (!hasUnsavedChanges && !lastSavedAt)}
                onClick={() => {
                  if (
                    !confirm(
                      'Discard unpublished changes? The dashboard reverts to the last published version.',
                    )
                  )
                    return;
                  // A save still inside its debounce would race the discard.
                  draftAutosave.cancel();
                  discardDraft.mutate(undefined, {
                    // The discard did not happen: save the edit after all.
                    onError: () => draftAutosave.resume(),
                    onSuccess: (fresh) => {
                      const pub = fresh.currentVersion?.definition;
                      if (pub && pub.layout.kind === 'html') {
                        setTitle(pub.layout.title);
                        setHtml(pub.layout.html);
                        setCss(pub.layout.css ?? '');
                        setLibraries(pub.layout.libraries ?? []);
                        setBindings(pub.layout.datasets);
                        // Reverting is not an edit: without this the reset
                        // state auto-saved straight back into the draft.
                        draftAutosave.resetBaseline(
                          htmlSnapshot({
                            title: pub.layout.title,
                            html: pub.layout.html,
                            css: pub.layout.css ?? '',
                            libraries: pub.layout.libraries ?? [],
                            bindings: pub.layout.datasets,
                          }),
                        );
                      }
                      setHasUnsavedChanges(false);
                      setLastSavedAt(null);
                    },
                  });
                }}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm canvas-action-publish"
                disabled={publish.isPending || saveDraft.isPending}
                onClick={() => {
                  publish.mutate(
                    {},
                    {
                      onSuccess: () => {
                        setHasUnsavedChanges(false);
                        setLastSavedAt(null);
                      },
                    },
                  );
                }}
                title={`Promote the current draft to v${currentVersion + 1} so kiosks + embeds start serving it`}
              >
                {publish.isPending ? 'Publishing…' : `Publish v${currentVersion + 1}`}
              </button>
            </div>
          </div>

          {pane === 'preview' && (
            <div className="dash-preview">
              {!previewUrl ? (
                <div className="text-dim" style={{ padding: 20 }}>
                  Minting preview token…
                </div>
              ) : (
                <iframe
                  ref={iframeRef}
                  className="dash-preview-iframe"
                  src={previewUrl}
                  title="Preview"
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          )}

          {pane === 'html' && (
            <CodeEditor
              filename="template.html"
              language="html"
              value={html}
              onChange={setHtml}
              meta={
                <>
                  Datasets exposed under <code>datasets.&lt;key&gt;</code>. Edit the title in{' '}
                  <strong>Settings</strong>.
                </>
              }
            />
          )}

          {pane === 'css' && (
            <CodeEditor
              filename="styles.css"
              language="css"
              value={css}
              onChange={setCss}
              placeholder="/* Your CSS — scoped to this dashboard */"
              meta="Standard CSS. The preview iframe applies it directly."
            />
          )}
        </div>

        {/* RIGHT panel removed — datasets now open as a modal so they
            get full-screen real estate instead of cramming into a
            ~250px sidebar. See <BindingsModal/> below. */}
      </div>

      {publish.error && (
        <div className="alert alert--danger" style={{ margin: 'var(--space-3)' }}>
          <pre>
            {JSON.stringify(
              (publish.error as Error & { body?: unknown }).body ?? String(publish.error),
              null,
              2,
            )}
          </pre>
        </div>
      )}

      <DashboardDesignerStyles />

      {datasetsOpen && (
        <BindingsModal
          bindings={bindings}
          onChange={setBindings}
          datasets={datasetCatalog ?? []}
          onClose={() => setDatasetsOpen(false)}
        />
      )}

      {librariesOpen && (
        <LibrariesPopover
          libraries={libraries}
          toggle={toggleLib}
          anchorRef={librariesBtnRef}
          onClose={() => setLibrariesOpen(false)}
        />
      )}

      {helpOpen && (
        <HelpModal
          onClose={() => setHelpOpen(false)}
          onPickPrompt={(p) => {
            setPrompt(p);
            setHelpOpen(false);
            if (chatCollapsed) setChatCollapsed(false);
          }}
        />
      )}

      {fsPreview && previewUrl && (
        <FullscreenPreview onClose={() => setFsPreview(false)} title={definition.layout.title}>
          <iframe
            title="Fullscreen dashboard preview"
            src={previewUrl}
            sandbox="allow-scripts allow-same-origin"
            style={{ background: '#0a0b0e' }}
          />
        </FullscreenPreview>
      )}
    </div>
  );
}

// ─── Bindings sidebar ──────────────────────────────────────────────

function BindingsModal({
  bindings,
  onChange,
  datasets,
  onClose,
}: {
  bindings: DatasetBinding[];
  onChange(next: DatasetBinding[]): void;
  datasets: DatasetSummary[];
  onClose(): void;
}): JSX.Element {
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  function updateBinding(idx: number, patch: Partial<DatasetBinding>): void {
    onChange(bindings.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function removeBinding(idx: number): void {
    onChange(bindings.filter((_, i) => i !== idx));
  }

  function addBinding(key: string, datasetName: string): void {
    const ds = datasets.find((d) => d.name === datasetName);
    const cols = ds?.columns.map((c: { name: string }) => c.name) ?? [];
    const next: DatasetBinding = {
      key,
      datasetName,
      ...(cols.length > 0 ? { columns: cols } : {}),
      limit: 500,
    };
    onChange([...bindings, next]);
    setAdding(false);
  }

  return createPortal(
    <div className="bindings-modal-backdrop" onMouseDown={onClose}>
      <div
        className="bindings-modal"
        role="dialog"
        aria-label="Datasets"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="bindings-modal-head">
          <div>
            <h2>Datasets</h2>
            <p>
              Each binding becomes <code>datasets.&lt;key&gt;</code> in your template. Bind to
              anything in the catalog — query results, KPI snapshots, framework rollups. Cards below
              are the current bindings; add more with the button on the right.
            </p>
          </div>
          <div className="bindings-modal-actions">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setAdding(true)}
              disabled={adding}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden
              >
                <path d="M7 3v8M3 7h8" />
              </svg>
              Add binding
            </button>
            <button
              type="button"
              className="btn-icon"
              onClick={onClose}
              aria-label="Close datasets"
            >
              ×
            </button>
          </div>
        </header>

        <div className="bindings-modal-body">
          {bindings.length === 0 && !adding && (
            <div className="dash-bindings-empty">
              <div className="dash-bindings-empty-icon" aria-hidden>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 22 22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <ellipse cx="11" cy="4.5" rx="7" ry="2.5" />
                  <path d="M4 4.5v6c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-6" />
                  <path d="M4 10.5v6c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-6" />
                </svg>
              </div>
              <div className="dash-bindings-empty-title">No bindings yet</div>
              <p>
                Add at least one dataset — the rendered HTML will then have rows available under{' '}
                <code>datasets.&lt;key&gt;</code>.
              </p>
            </div>
          )}

          <ul className="dash-binding-list">
            {bindings.map((b, i) => (
              <li key={i} className="dash-binding-card">
                <div className="dash-binding-card-head">
                  <div className="dash-binding-card-key">
                    <span className="dash-binding-card-prefix">datasets.</span>
                    <span className="dash-binding-card-keyname">{b.key}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-icon dash-binding-card-remove"
                    onClick={() => removeBinding(i)}
                    aria-label="Remove binding"
                    title="Remove binding"
                  >
                    ×
                  </button>
                </div>
                <div className="dash-binding-card-source">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    aria-hidden
                  >
                    <ellipse cx="6" cy="2.5" rx="4" ry="1.3" />
                    <path d="M2 2.5v7c0 .72 1.79 1.3 4 1.3s4-.58 4-1.3v-7" />
                  </svg>
                  <span>{b.datasetName}</span>
                </div>
                <div className="dash-binding-card-fields">
                  <div className="field" style={{ margin: 0 }}>
                    <label className="field-label">Key</label>
                    <input
                      className="input input--sm input--mono"
                      value={b.key}
                      onChange={(e) => updateBinding(i, { key: e.target.value })}
                    />
                  </div>
                  <div className="field" style={{ margin: 0 }}>
                    <label className="field-label">Row cap</label>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      className="input input--sm"
                      value={b.limit ?? 500}
                      onChange={(e) =>
                        updateBinding(i, { limit: parseInt(e.target.value || '500', 10) })
                      }
                    />
                  </div>
                </div>
                {b.columns && b.columns.length > 0 && (
                  <div className="dash-binding-card-cols">
                    <div className="dash-binding-card-cols-label">Columns ({b.columns.length})</div>
                    <div className="dash-binding-card-cols-chips">
                      {b.columns.slice(0, 8).map((c) => (
                        <span key={c} className="dash-binding-col-chip">
                          {c}
                        </span>
                      ))}
                      {b.columns.length > 8 && (
                        <span className="dash-binding-col-more">+{b.columns.length - 8} more</span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {adding && (
            <AddBindingCard
              datasets={datasets}
              existingKeys={bindings.map((b) => b.key)}
              onCancel={() => setAdding(false)}
              onSave={addBinding}
            />
          )}

          <p className="dash-bindings-footnote">
            Need the Handlebars cheat sheet? Click <strong>Help</strong> in the canvas toolbar.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Libraries popover ─────────────────────────────────────────────

function LibrariesPopover({
  libraries,
  toggle,
  anchorRef,
  onClose,
}: {
  libraries: RenderLibrary[];
  toggle(lib: RenderLibrary): void;
  anchorRef: React.RefObject<HTMLButtonElement>;
  onClose(): void;
}): JSX.Element | null {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const popWidth = 320;
    setPos({
      top: r.bottom + 6,
      left: Math.max(8, Math.min(window.innerWidth - popWidth - 8, r.left)),
    });
  }, [anchorRef]);

  useEffect(() => {
    function onDoc(e: MouseEvent): void {
      const tgt = e.target as globalThis.Node | null;
      if (popRef.current?.contains(tgt as globalThis.Node)) return;
      if (anchorRef.current?.contains(tgt as globalThis.Node)) return;
      onClose();
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose, anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={popRef}
      className="libraries-popover"
      role="dialog"
      aria-label="Render libraries"
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
    >
      <header className="libraries-popover-head">
        <h3>Render libraries</h3>
        <p>
          Toggle the runtime libraries the rendered HTML can reference. Claude uses these as
          building blocks when designing the layout.
        </p>
      </header>
      <div className="libraries-popover-body">
        {LIBRARY_OPTIONS.map((lib) => {
          const on = libraries.includes(lib.id);
          return (
            <button
              key={lib.id}
              type="button"
              className={'ai-lib-toggle' + (on ? ' ai-lib-toggle--on' : '')}
              onClick={() => toggle(lib.id)}
              aria-pressed={on}
            >
              <span className="ai-lib-icon" aria-hidden>
                {lib.icon}
              </span>
              <span className="ai-lib-text">
                <span className="ai-lib-name">{lib.label}</span>
                <span className="ai-lib-desc">{lib.desc}</span>
              </span>
              <span className="ai-lib-dot" aria-hidden />
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

// ─── Help modal ────────────────────────────────────────────────────

function HelpModal({
  onClose,
  onPickPrompt,
}: {
  onClose(): void;
  onPickPrompt(prompt: string): void;
}): JSX.Element {
  useEffect(() => {
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return createPortal(
    <div className="help-modal-backdrop" onMouseDown={onClose}>
      <div
        className="help-modal"
        role="dialog"
        aria-label="Dashboard designer help"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="help-modal-head">
          <div>
            <h2>Designer reference</h2>
            <p>
              Handlebars helpers, example prompts, and library cheat sheet — everything you need to
              keep authoring without scrolling away.
            </p>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close help">
            ×
          </button>
        </header>

        <div className="help-modal-body">
          <section className="help-section">
            <h3>Start from an example</h3>
            <p className="help-section-hint">
              Click any card to drop the prompt into chat (and open chat if it's closed).
            </p>
            <ul className="ai-example-prompts">
              {STARTER_PROMPTS.map((p) => (
                <li
                  key={p.title}
                  className="ai-example-card"
                  onClick={() => onPickPrompt(p.prompt)}
                >
                  <div className="ai-example-card-icon" aria-hidden>
                    {p.icon}
                  </div>
                  <div className="ai-example-card-body">
                    <div className="ai-example-card-title">{p.title}</div>
                    <div className="ai-example-card-desc">{p.desc}</div>
                  </div>
                  <div className="ai-example-card-arrow" aria-hidden>
                    →
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="help-section">
            <h3>Handlebars cheat sheet</h3>
            <p className="help-section-hint">
              Datasets are exposed as <code>datasets.&lt;key&gt;</code> (an array of rows).
            </p>
            <div className="help-snippets-grid">
              {HANDLEBARS_SNIPPETS.map((s) => (
                <div key={s.title} className="dash-help-snippet">
                  <div className="dash-help-snippet-title">{s.title}</div>
                  <pre className="dash-help-snippet-code">{s.code}</pre>
                </div>
              ))}
            </div>
          </section>

          <section className="help-section">
            <h3>Render libraries</h3>
            <p className="help-section-hint">
              Toggle from the <strong>Libraries</strong> button in the canvas toolbar.
            </p>
            <ul className="help-lib-list">
              {LIBRARY_OPTIONS.map((lib) => (
                <li key={lib.id}>
                  <span className="help-lib-name">{lib.label}</span>
                  <span className="help-lib-desc">{lib.desc}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AddBindingCard({
  datasets,
  existingKeys,
  onSave,
  onCancel,
}: {
  datasets: DatasetSummary[];
  existingKeys: string[];
  onSave(key: string, datasetName: string): void;
  onCancel(): void;
}): JSX.Element {
  const [key, setKey] = useState('');
  const [datasetName, setDatasetName] = useState('');

  const canSave =
    /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) && !existingKeys.includes(key) && !!datasetName;

  return (
    <div className="dash-binding-card" style={{ borderColor: 'var(--color-accent-border)' }}>
      <div className="field" style={{ margin: '0 0 8px' }}>
        <label className="field-label">Key</label>
        <input
          className="input input--sm input--mono"
          placeholder="devices"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          autoFocus
        />
        {key && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) && (
          <div className="text-xs" style={{ color: 'var(--color-danger)', marginTop: 4 }}>
            Must be a valid identifier: letters / digits / underscore.
          </div>
        )}
        {key && existingKeys.includes(key) && (
          <div className="text-xs" style={{ color: 'var(--color-danger)', marginTop: 4 }}>
            Already used.
          </div>
        )}
      </div>
      <div className="field" style={{ margin: '0 0 10px' }}>
        <label className="field-label">Dataset</label>
        <select
          className="select input--sm"
          value={datasetName}
          onChange={(e) => setDatasetName(e.target.value)}
        >
          <option value="">Select…</option>
          {datasets.map((d) => (
            <option key={d.id} value={d.name}>
              {d.name} ({d.mode})
            </option>
          ))}
        </select>
      </div>
      <div className="row row--end" style={{ gap: 6 }}>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!canSave}
          onClick={() => onSave(key, datasetName)}
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Code editor ───────────────────────────────────────────────────

/**
 * Full-height code editor: titled file-bar at the top, monospace
 * textarea below, footer with character count + free-form meta. Used
 * by the HTML and CSS panes — drops the inline Title input that used
 * to clutter the HTML view (now lives in Settings, where it belongs).
 *
 * Not a real syntax-highlighting editor — just a polished textarea
 * with consistent dark styling, tab-stop friendliness, and a
 * recognizable IDE-style chrome (file pill + language tag).
 */
function CodeEditor({
  filename,
  language,
  value,
  onChange,
  placeholder,
  meta,
}: {
  filename: string;
  language: 'html' | 'css';
  value: string;
  onChange(next: string): void;
  placeholder?: string;
  meta?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="code-editor">
      <header className="code-editor-bar">
        <span className="code-editor-filename">
          <span className={'code-editor-dot code-editor-dot--' + language} aria-hidden />
          {filename}
        </span>
        <span className="code-editor-lang">{language.toUpperCase()}</span>
      </header>
      <textarea
        className="code-editor-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        placeholder={placeholder ?? ''}
        // Reasonable defaults for editing code with the keyboard
        autoCapitalize="off"
        autoCorrect="off"
        wrap="off"
      />
      <footer className="code-editor-footer">
        <span className="code-editor-meta">{meta}</span>
        <span className="code-editor-count">{value.length.toLocaleString()} chars</span>
      </footer>
    </div>
  );
}

// ─── Hooks ─────────────────────────────────────────────────────────

/** Boolean useState whose value is persisted to localStorage. Falls
 *  back to the default on SSR / private-mode / quota-exceeded. */
function usePersistedFlag(
  key: string,
  defaultValue: boolean,
): [boolean, (v: boolean | ((p: boolean) => boolean)) => void] {
  const [value, setValueRaw] = useState<boolean>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw == null ? defaultValue : raw === '1';
    } catch {
      return defaultValue;
    }
  });
  const setValue = (v: boolean | ((p: boolean) => boolean)): void => {
    setValueRaw((prev) => {
      const next = typeof v === 'function' ? (v as (p: boolean) => boolean)(prev) : v;
      try {
        window.localStorage.setItem(key, next ? '1' : '0');
      } catch {
        /* swallow */
      }
      return next;
    });
  };
  return [value, setValue];
}

// ─── Static data ───────────────────────────────────────────────────

const LIBRARY_OPTIONS: ReadonlyArray<{
  id: RenderLibrary;
  label: string;
  desc: string;
  icon: string;
}> = [
  { id: 'chartjs', label: 'Chart.js', desc: 'Line / bar / pie / sparkline charts', icon: '📊' },
  { id: 'lucide', label: 'Lucide', desc: 'Minimal stroked icon set', icon: '✦' },
  {
    id: 'fontawesome',
    label: 'Font Awesome',
    desc: 'Solid icon library, broad coverage',
    icon: '✺',
  },
];

const STARTER_PROMPTS: ReadonlyArray<{
  title: string;
  desc: string;
  icon: string;
  prompt: string;
}> = [
  {
    title: 'NOC fleet-health wall',
    desc: 'Big total + 3 status cards + bar by location',
    icon: '◉',
    prompt:
      'NOC fleet health wall: big total-devices number at the top, then a 3-row grid of KPI cards for online/offline/degraded, and a bar chart of devices by location below. Green pulse for liveness.',
  },
  {
    title: 'Executive one-number screen',
    desc: 'Headline KPI + comparison + small trend',
    icon: '▲',
    prompt:
      'Executive single-screen: one giant headline KPI, a subtitle with a comparison, and a small trend chart. No other chrome.',
  },
  {
    title: 'Regional status wall',
    desc: 'Per-region card grid with sparkline',
    icon: '◐',
    prompt:
      'Regional status wall: one card per region, each showing online/offline counts and a sparkline. Red ring around any region with offline devices.',
  },
];

const HANDLEBARS_SNIPPETS: ReadonlyArray<{ title: string; code: string }> = [
  {
    title: 'Iterate rows',
    code: `{{#each datasets.rows}}
  {{this.name}} — {{this.status}}
{{/each}}`,
  },
  {
    title: 'Aggregate',
    code: `{{sum datasets.rows "amount"}}
{{count datasets.rows}}
{{distinctCount datasets.rows "region"}}`,
  },
  {
    title: 'Group by',
    code: `{{#group datasets.rows "region"}}
  {{@key}}: {{count items}}
{{/group}}`,
  },
  {
    title: 'Format helpers',
    code: `{{format value "int"}}     · 12,345
{{format value "percent"}} · 87.3%
{{formatDate ts "YYYY-MM-DD"}}`,
  },
];

// ─── Helpers ───────────────────────────────────────────────────────

/**
 * Shows the user whether their latest edit has been saved to the draft,
 * or whether there are unsaved changes in-flight. Publish is separate
 * (explicit button) — this is purely about the auto-saved draft buffer.
 */
function DraftIndicator({
  saving,
  unsaved,
  lastSavedAt,
}: {
  saving: boolean;
  unsaved: boolean;
  lastSavedAt: Date | null;
}): JSX.Element {
  if (saving) {
    return <span className="draft-indicator draft-indicator--saving">◌ Saving draft…</span>;
  }
  if (unsaved) {
    return <span className="draft-indicator draft-indicator--unsaved">● Unsaved changes</span>;
  }
  if (lastSavedAt) {
    return (
      <span className="draft-indicator draft-indicator--saved" title={lastSavedAt.toLocaleString()}>
        ✓ Draft saved
      </span>
    );
  }
  return <></>;
}

/** The auto-save change detector: the designer state that a draft holds. */
function htmlSnapshot(s: {
  title: string;
  html: string;
  css: string;
  libraries: RenderLibrary[];
  bindings: DatasetBinding[];
}): string {
  return JSON.stringify({
    title: s.title,
    html: s.html,
    css: s.css,
    libraries: s.libraries,
    bindings: s.bindings,
  });
}

function cacheBust(...parts: unknown[]): string {
  // Cheap hash so the iframe src changes when any input changes.
  let h = 0;
  const joined = JSON.stringify(parts);
  for (let i = 0; i < joined.length; i++) {
    h = (h * 31 + joined.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

function mockSamples(columns: string[]): Array<Record<string, unknown>> {
  if (columns.length === 0) return [{ sample: 1 }];
  return [1, 2].map((i) => Object.fromEntries(columns.map((c) => [c, inferSample(c, i)])));
}

function inferSample(field: string, i: number): unknown {
  if (/count|total|num|amount|n$|qty|devices/i.test(field)) return i * 3 + 1;
  if (/region/i.test(field)) return ['us-east', 'us-west'][i - 1] ?? 'us-east';
  if (/status/i.test(field)) return ['online', 'offline'][i - 1] ?? 'online';
  return `Sample ${i}`;
}

function summarizeAssistant(raw: string): string {
  const first = raw.split('\n').find((l) => l.trim().length > 0) ?? '';
  const approxChars = raw.length.toLocaleString();
  return `Updated the dashboard (${approxChars} chars). Top of output: ${first.slice(0, 120)}…`;
}

function DashboardDesignerStyles(): JSX.Element {
  return (
    <style>{`
      .dash-designer {
        position: relative;
        display: grid;
        /* Inline grid-template-columns from useSplitPaneSize controls the
           actual widths; collapsed classes only hide the pane visually. */
        grid-template-columns: 26% 4px 1fr 4px 20%;
        min-height: 640px;
        height: calc(100vh - 260px);
      }
      .dash-designer--chat-collapsed .ai-chat,
      .dash-designer--bindings-collapsed .dash-bindings {
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }

      /* ── Chat column constraints — CRITICAL ───────────────────
         Without min-height: 0 on a flex/grid child, content forces
         the column to expand past the grid cell. That overflow then
         pushes the iframe / canvas down below the viewport AND makes
         the chatEndRef.scrollIntoView call yank the whole page,
         hiding the Publish toolbar. The reports designer
         (AiDesignerPanel.tsx) has the same rules — keep them in sync. */
      .ai-chat {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
      }
      .ai-chat-header {
        flex-shrink: 0;
      }
      .ai-libraries {
        flex-shrink: 0;
      }
      .ai-chat-input {
        flex-shrink: 0;
      }
      .ai-chat-log {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
      }
      .dash-canvas {
        min-height: 0;
        overflow: hidden;
      }
      .dash-bindings-reopen {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 5;
        width: 32px;
        height: 32px;
        border-radius: var(--radius-md);
        background: var(--color-surface-2);
        border: 1px solid var(--color-border);
        color: var(--color-text-secondary);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: var(--shadow-sm);
      }
      .dash-bindings-reopen:hover {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
      }
      .dash-canvas {
        display: flex;
        flex-direction: column;
        background: var(--color-surface-1);
        min-height: 0;
        border-left: 1px solid var(--color-border-subtle);
      }
      .dash-preview {
        flex: 1;
        min-height: 0;
        background: #0a0b0e;
        padding: 12px;
        display: flex;
      }
      .dash-preview-iframe {
        background: #0a0b0e;
        border: 1px solid var(--color-border);
        border-radius: 8px;
        width: 100%;
        height: 100%;
        box-shadow: 0 12px 40px rgba(0,0,0,0.5);
      }
      .dash-bindings {
        background: var(--color-surface-1);
        border-left: 1px solid var(--color-border-subtle);
        padding: 16px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      .dash-bindings-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .dash-bindings-title h3 {
        margin: 0;
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: -0.01em;
      }
      .dash-bindings-title p {
        margin: 4px 0 0;
        font-size: 11.5px;
        line-height: 1.45;
        color: var(--color-text-tertiary);
      }
      .dash-bindings-title code {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 11px;
        padding: 1px 5px;
        background: var(--color-surface-3);
        border-radius: 3px;
        color: var(--color-text-secondary);
      }
      .dash-bindings-actions {
        display: flex;
        gap: 4px;
        align-items: center;
        flex-shrink: 0;
      }
      .dash-bindings-actions .btn--primary {
        gap: 4px;
      }
      .dash-bindings-empty {
        text-align: center;
        padding: 20px 12px;
        border: 1px dashed var(--color-border-subtle);
        border-radius: var(--radius-md);
        background: var(--color-surface-2);
      }
      .dash-bindings-empty-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--color-surface-3);
        color: var(--color-text-tertiary);
        margin-bottom: 10px;
      }
      .dash-bindings-empty-title {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-semibold, 600);
        color: var(--color-text-primary);
        margin-bottom: 4px;
      }
      .dash-bindings-empty p {
        margin: 0;
        font-size: 11.5px;
        color: var(--color-text-tertiary);
        line-height: 1.45;
      }
      .dash-bindings-empty code {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 11px;
        background: var(--color-surface-3);
        padding: 1px 5px;
        border-radius: 3px;
      }
      .dash-binding-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
      .dash-binding-card {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: 12px;
        transition: border-color 120ms ease, box-shadow 120ms ease;
      }
      .dash-binding-card:hover {
        border-color: var(--color-border);
      }
      .dash-binding-card-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }
      .dash-binding-card-key {
        display: inline-flex;
        align-items: baseline;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 12px;
        line-height: 1.2;
        background: var(--color-surface-3);
        padding: 3px 8px;
        border-radius: 4px;
        border: 1px solid var(--color-border-subtle);
      }
      .dash-binding-card-prefix {
        color: var(--color-text-tertiary);
      }
      .dash-binding-card-keyname {
        color: var(--color-accent-fg, #4f8eff);
        font-weight: var(--font-weight-semibold, 600);
      }
      .dash-binding-card-remove {
        font-size: 16px;
        line-height: 1;
        color: var(--color-text-tertiary);
        width: 22px;
        height: 22px;
      }
      .dash-binding-card-remove:hover {
        color: var(--color-danger);
        background: color-mix(in srgb, var(--color-danger) 12%, transparent);
      }
      .dash-binding-card-source {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--color-text-tertiary);
        font-size: 11px;
        margin-bottom: 10px;
      }
      .dash-binding-card-source span {
        color: var(--color-text-secondary);
        font-weight: var(--font-weight-medium, 500);
      }
      .dash-binding-card-fields {
        display: grid;
        grid-template-columns: 1fr 80px;
        gap: 8px;
      }
      .dash-binding-card-cols {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid var(--color-border-subtle);
      }
      .dash-binding-card-cols-label {
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        margin-bottom: 6px;
      }
      .dash-binding-card-cols-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }
      .dash-binding-col-chip {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 10px;
        padding: 2px 6px;
        background: var(--color-surface-3);
        border: 1px solid var(--color-border-subtle);
        border-radius: 3px;
        color: var(--color-text-secondary);
      }
      .dash-binding-col-more {
        font-size: 10px;
        color: var(--color-text-tertiary);
        align-self: center;
        padding: 0 2px;
      }
      .dash-bindings-help {
        margin-top: auto;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        overflow: hidden;
      }
      .dash-bindings-help-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 10px 12px;
        background: transparent;
        border: none;
        color: var(--color-text-primary);
        font-size: 12px;
        font-weight: var(--font-weight-semibold, 600);
        cursor: pointer;
        text-align: left;
      }
      .dash-bindings-help-toggle:hover {
        background: var(--color-surface-3);
      }
      .dash-bindings-help-pill {
        margin-left: auto;
        font-size: 9px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        background: var(--color-surface-3);
        padding: 2px 6px;
        border-radius: 3px;
      }
      .dash-bindings-help-body {
        padding: 4px 12px 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .dash-help-snippet-title {
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        margin-bottom: 4px;
      }
      .dash-help-snippet-code {
        margin: 0;
        padding: 8px 10px;
        background: #0d1117;
        color: #d4d8e0;
        border: 1px solid var(--color-border-subtle);
        border-radius: 4px;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 10.5px;
        line-height: 1.55;
        overflow-x: auto;
        white-space: pre;
      }

      /* ── Libraries panel ─────────────────────────────────────── */
      .ai-libraries {
        padding: 12px 14px 14px;
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
      }
      .ai-libraries-head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }
      .ai-libraries-label {
        font-size: 11px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-secondary);
      }
      .ai-libraries-count {
        font-size: 10px;
        color: var(--color-text-tertiary);
      }
      .ai-libraries-hint {
        margin: 4px 0 10px;
        font-size: 11px;
        line-height: 1.45;
        color: var(--color-text-tertiary);
      }
      .ai-libraries-toggles {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .ai-lib-toggle {
        display: grid;
        grid-template-columns: 22px 1fr 10px;
        align-items: center;
        gap: 8px;
        padding: 7px 10px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm, 4px);
        cursor: pointer;
        text-align: left;
        font: inherit;
        color: var(--color-text-secondary);
        transition: background-color 100ms ease, border-color 100ms ease;
      }
      .ai-lib-toggle:hover {
        border-color: var(--color-border);
      }
      .ai-lib-toggle--on {
        background: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 10%, var(--color-surface-2));
        border-color: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 35%, transparent);
        color: var(--color-text-primary);
      }
      .ai-lib-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 4px;
        background: var(--color-surface-3);
        font-size: 13px;
        color: var(--color-text-secondary);
      }
      .ai-lib-toggle--on .ai-lib-icon {
        background: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 20%, transparent);
        color: var(--color-accent-fg, #4f8eff);
      }
      .ai-lib-text {
        display: flex;
        flex-direction: column;
        gap: 1px;
        min-width: 0;
      }
      .ai-lib-name {
        font-size: 12px;
        font-weight: var(--font-weight-semibold, 600);
      }
      .ai-lib-desc {
        font-size: 10.5px;
        color: var(--color-text-tertiary);
        line-height: 1.3;
      }
      .ai-lib-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--color-border);
        transition: background-color 100ms ease;
      }
      .ai-lib-toggle--on .ai-lib-dot {
        background: var(--color-accent-fg, #4f8eff);
        box-shadow: 0 0 6px color-mix(in srgb, var(--color-accent-fg, #4f8eff) 60%, transparent);
      }

      /* ── Describe-the-dashboard empty state ──────────────────── */
      .ai-chat-empty {
        padding: 28px 22px;
        text-align: center;
        max-width: 460px;
        margin: 0 auto;
      }
      .ai-chat-empty-spark {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 14%, transparent);
        color: var(--color-accent-fg, #4f8eff);
        margin-bottom: 14px;
      }
      .ai-chat-empty h3 {
        margin: 0 0 6px;
        font-size: 16px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: -0.01em;
      }
      .ai-chat-empty-lede {
        margin: 0 0 18px;
        font-size: 12.5px;
        line-height: 1.5;
        color: var(--color-text-tertiary);
      }
      .ai-example-header {
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        margin: 6px 0 8px;
        text-align: left;
      }
      .ai-example-prompts {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .ai-example-card {
        display: grid;
        grid-template-columns: 28px 1fr 16px;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        text-align: left;
        cursor: pointer;
        transition: border-color 100ms ease, background-color 100ms ease, transform 100ms ease;
      }
      .ai-example-card:hover {
        border-color: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 50%, transparent);
        background: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 6%, var(--color-surface-2));
        transform: translateY(-1px);
      }
      .ai-example-card-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        background: var(--color-surface-3);
        color: var(--color-accent-fg, #4f8eff);
        font-size: 14px;
      }
      .ai-example-card-title {
        font-size: 12.5px;
        font-weight: var(--font-weight-semibold, 600);
        color: var(--color-text-primary);
      }
      .ai-example-card-desc {
        font-size: 11px;
        color: var(--color-text-tertiary);
        margin-top: 1px;
      }
      .ai-example-card-arrow {
        color: var(--color-text-tertiary);
        font-size: 14px;
        line-height: 1;
        transition: transform 120ms ease, color 120ms ease;
      }
      .ai-example-card:hover .ai-example-card-arrow {
        color: var(--color-accent-fg, #4f8eff);
        transform: translateX(2px);
      }

      /* ── Canvas-header authoring toolbar ─────────────────────── */
      .designer-toolbar {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 2px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        margin: 0 8px;
      }
      .designer-toolbar-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 9px;
        background: transparent;
        border: none;
        border-radius: calc(var(--radius-md) - 2px);
        color: var(--color-text-secondary);
        font: inherit;
        font-size: 12px;
        font-weight: var(--font-weight-medium, 500);
        cursor: pointer;
        transition: background-color 100ms ease, color 100ms ease;
      }
      .designer-toolbar-btn:hover {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
      }
      .designer-toolbar-btn--on {
        background: var(--color-accent-fg, #4f8eff);
        color: #fff;
      }
      .designer-toolbar-btn--on:hover {
        background: var(--color-accent-fg, #4f8eff);
        color: #fff;
        filter: brightness(1.08);
      }
      .designer-toolbar-btn svg {
        opacity: 0.85;
      }
      .designer-toolbar-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 16px;
        height: 16px;
        padding: 0 5px;
        border-radius: 8px;
        background: var(--color-surface-1);
        color: var(--color-text-tertiary);
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        line-height: 1;
      }
      .designer-toolbar-btn--on .designer-toolbar-count {
        background: rgba(255,255,255,0.25);
        color: #fff;
      }

      /* ── Libraries popover ──────────────────────────────────── */
      .libraries-popover {
        width: 320px;
        background: var(--color-surface-1);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-md);
        box-shadow: 0 12px 36px rgba(0,0,0,0.35);
        z-index: 200;
        overflow: hidden;
      }
      .libraries-popover-head {
        padding: 12px 14px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .libraries-popover-head h3 {
        margin: 0;
        font-size: 13px;
        font-weight: var(--font-weight-semibold, 600);
      }
      .libraries-popover-head p {
        margin: 4px 0 0;
        font-size: 11.5px;
        line-height: 1.4;
        color: var(--color-text-tertiary);
      }
      .libraries-popover-body {
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 12px;
      }

      /* ── Help modal ─────────────────────────────────────────── */
      .help-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(7,9,12,0.55);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 300;
        padding: 24px;
      }
      .help-modal {
        background: var(--color-surface-1);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg, 8px);
        box-shadow: 0 24px 80px rgba(0,0,0,0.55);
        width: min(820px, 100%);
        max-height: calc(100vh - 48px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .help-modal-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 18px 22px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .help-modal-head h2 {
        margin: 0;
        font-size: 17px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: -0.01em;
      }
      .help-modal-head p {
        margin: 6px 0 0;
        font-size: 12.5px;
        line-height: 1.45;
        color: var(--color-text-tertiary);
        max-width: 640px;
      }
      .help-modal-body {
        padding: 18px 22px 24px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 22px;
      }
      .help-section h3 {
        margin: 0 0 4px;
        font-size: 11px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }
      .help-section-hint {
        margin: 0 0 10px;
        font-size: 12px;
        color: var(--color-text-secondary);
      }
      .help-section-hint code {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 11.5px;
        background: var(--color-surface-3);
        padding: 1px 5px;
        border-radius: 3px;
      }
      .help-snippets-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 10px;
      }
      .help-lib-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 8px;
      }
      .help-lib-list li {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 10px 12px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
      }
      .help-lib-name {
        font-size: 12.5px;
        font-weight: var(--font-weight-semibold, 600);
      }
      .help-lib-desc {
        font-size: 11.5px;
        color: var(--color-text-tertiary);
      }

      .dash-bindings-footnote {
        margin: 12px 0 0;
        padding: 10px 12px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        font-size: 11.5px;
        color: var(--color-text-tertiary);
        line-height: 1.45;
      }
      .dash-bindings-footnote strong {
        color: var(--color-text-secondary);
        font-weight: var(--font-weight-semibold, 600);
      }

      /* ── Tightened canvas header ─────────────────────────────── */
      .ai-canvas-header {
        gap: 12px;
        padding: 6px 12px;
        min-height: 48px;
        flex-wrap: nowrap;
      }

      /* Right-side action cluster. flex-shrink: 0 so it never wraps
         even when middle content is wide — the toolbar buttons in
         the middle stay on the same line as Publish. */
      .canvas-actions {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
        flex-shrink: 0;
      }
      .canvas-action-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        background: transparent;
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm, 4px);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
      }
      .canvas-action-icon:hover:not(:disabled) {
        background: var(--color-surface-2);
        color: var(--color-text-primary);
        border-color: var(--color-border);
      }
      .canvas-action-icon:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .canvas-action-publish {
        font-weight: var(--font-weight-semibold, 600);
      }
      /* Save-as-framework — labeled secondary button matching the
         Fullscreen treatment so the right cluster reads as a row of
         peer actions, with Publish promoted by accent color. */
      .canvas-action-save {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm, 4px);
        color: var(--color-text-secondary);
        font: inherit;
        font-size: 12px;
        font-weight: var(--font-weight-medium, 500);
        cursor: pointer;
        white-space: nowrap;
        transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
      }
      .canvas-action-save:hover:not(:disabled) {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
        border-color: var(--color-border);
      }
      .canvas-action-save:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
      /* Bigger, labeled fullscreen button — the icon-only version
         was easy to miss. Inline label makes the action obvious. */
      .canvas-action-fullscreen {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm, 4px);
        color: var(--color-text-secondary);
        font: inherit;
        font-size: 12px;
        font-weight: var(--font-weight-medium, 500);
        cursor: pointer;
        transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
      }
      .canvas-action-fullscreen:hover {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
        border-color: var(--color-border);
      }

      /* ── Datasets modal ──────────────────────────────────────── */
      .bindings-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(7,9,12,0.55);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 300;
        padding: 24px;
      }
      .bindings-modal {
        background: var(--color-surface-1);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg, 8px);
        box-shadow: 0 24px 80px rgba(0,0,0,0.55);
        width: min(960px, 100%);
        max-height: calc(100vh - 48px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .bindings-modal-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 18px 22px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .bindings-modal-head h2 {
        margin: 0;
        font-size: 17px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: -0.01em;
      }
      .bindings-modal-head p {
        margin: 6px 0 0;
        font-size: 12.5px;
        line-height: 1.5;
        color: var(--color-text-tertiary);
        max-width: 640px;
      }
      .bindings-modal-head code {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 11.5px;
        background: var(--color-surface-3);
        padding: 1px 5px;
        border-radius: 3px;
        color: var(--color-text-secondary);
      }
      .bindings-modal-actions {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
      }
      .bindings-modal-actions .btn--primary {
        gap: 4px;
      }
      .bindings-modal-body {
        padding: 18px 22px 22px;
        overflow-y: auto;
      }
      .bindings-modal-body .dash-binding-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 10px;
      }
      .bindings-modal-body .dash-binding-card {
        background: var(--color-surface-2);
      }
      .bindings-modal-body .dash-bindings-empty {
        max-width: 480px;
        margin: 16px auto;
      }

      /* ── View-mode segmented switch (Preview / HTML / CSS) ────
         Replaces the bulkier .tabs strip so the whole canvas
         toolbar fits on one line. */
      .view-mode-switch {
        display: inline-flex;
        align-items: center;
        gap: 1px;
        padding: 2px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        flex-shrink: 0;
      }
      .view-mode-btn {
        padding: 5px 11px;
        background: transparent;
        border: none;
        border-radius: calc(var(--radius-md) - 2px);
        color: var(--color-text-secondary);
        font: inherit;
        font-size: 12px;
        font-weight: var(--font-weight-medium, 500);
        cursor: pointer;
        transition: background-color 100ms ease, color 100ms ease;
      }
      .view-mode-btn:hover {
        color: var(--color-text-primary);
        background: var(--color-surface-3);
      }
      .view-mode-btn--on {
        background: var(--color-surface-1);
        color: var(--color-text-primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.18);
      }

      /* ── Code editor (HTML / CSS panes) ────────────────────────
         Was a bare textarea — now wrapped in an IDE-ish chrome
         with a filename bar and a footer. Same dark editor color
         palette as the Handlebars cheat snippets so the visual
         language is consistent. */
      .code-editor {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        background: #0d1117;
      }
      .code-editor-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 14px;
        background: #161b22;
        border-bottom: 1px solid #21262d;
        flex-shrink: 0;
      }
      .code-editor-filename {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 12px;
        font-weight: var(--font-weight-semibold, 600);
        color: #d4d8e0;
        letter-spacing: 0.01em;
      }
      .code-editor-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: #fb923c;
        flex-shrink: 0;
      }
      .code-editor-dot--css { background: #38bdf8; }
      .code-editor-lang {
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.08em;
        color: #8b949e;
        padding: 2px 6px;
        background: #21262d;
        border-radius: 3px;
      }
      .code-editor-input {
        flex: 1;
        min-height: 0;
        width: 100%;
        padding: 14px 16px;
        background: #0d1117;
        color: #d4d8e0;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 12.5px;
        line-height: 1.6;
        tab-size: 2;
        -moz-tab-size: 2;
        border: none;
        outline: none;
        resize: none;
        white-space: pre;
        overflow-wrap: normal;
        overflow-x: auto;
        caret-color: #4f8eff;
      }
      .code-editor-input::placeholder {
        color: #6e7681;
      }
      .code-editor-input::selection {
        background: rgba(79, 142, 255, 0.32);
      }
      .code-editor-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 7px 14px;
        background: #161b22;
        border-top: 1px solid #21262d;
        font-size: 11px;
        color: #8b949e;
        flex-shrink: 0;
      }
      .code-editor-meta code {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 11px;
        background: #21262d;
        color: #d4d8e0;
        padding: 1px 5px;
        border-radius: 3px;
      }
      .code-editor-meta strong {
        color: #d4d8e0;
        font-weight: var(--font-weight-semibold, 600);
      }
      .code-editor-count {
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
      }

      /* ── Consolidated dashboard header row ────────────────────
         Title + meta + tabs all on one line. Used by
         DashboardDetail. */
      .dashboard-detail-card .dashboard-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 10px 16px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .dashboard-header-title {
        display: flex;
        align-items: baseline;
        gap: 12px;
        min-width: 0;
        flex: 1;
      }
      .dashboard-header-title h2 {
        margin: 0;
        font-size: 15px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: -0.01em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dashboard-header-meta {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 11px;
        color: var(--color-text-tertiary);
        flex-shrink: 0;
      }
      .dashboard-header-badge {
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        font-size: 10px;
        font-weight: var(--font-weight-semibold, 600);
        padding: 2px 6px;
        border-radius: 3px;
        background: var(--color-surface-3);
        color: var(--color-text-secondary);
      }
      .dashboard-header-mode {
        font-size: 9px;
        font-weight: var(--font-weight-semibold, 600);
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 2px 6px;
        border-radius: 3px;
        border: 1px solid var(--color-border-subtle);
        background: var(--color-surface-2);
        color: var(--color-text-tertiary);
      }
      .dashboard-header-mode--kiosk {
        color: var(--color-accent-fg, #4f8eff);
        border-color: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 35%, transparent);
        background: color-mix(in srgb, var(--color-accent-fg, #4f8eff) 10%, transparent);
      }
      .dashboard-header-refresh {
        color: var(--color-text-tertiary);
      }
      .dashboard-header-tabs {
        display: inline-flex;
        gap: 2px;
        padding: 2px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        flex-shrink: 0;
      }
      .dashboard-header-tabs .tab {
        padding: 5px 11px;
        background: transparent;
        border: none;
        border-radius: calc(var(--radius-md) - 2px);
        color: var(--color-text-secondary);
        font: inherit;
        font-size: 12px;
        font-weight: var(--font-weight-medium, 500);
        cursor: pointer;
      }
      .dashboard-header-tabs .tab:hover {
        background: var(--color-surface-3);
        color: var(--color-text-primary);
      }
      .dashboard-header-tabs .tab--active {
        background: var(--color-surface-1);
        color: var(--color-text-primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.18);
      }

      /* Description strip under the dashboard header — concise prose
         explaining what the dashboard does. Clamps to 2 lines with an
         ellipsis; full text on hover via the title attribute. */
      .dashboard-header-description {
        font-size: 12.5px;
        line-height: 1.45;
        color: var(--color-text-secondary);
        padding: 6px 16px 12px;
        border-bottom: 1px solid var(--color-border-subtle);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `}</style>
  );
}
